import * as Sentry from '@sentry/react';
import SummaryDashboard, {
  BreakdownGroup,
  SummaryCardConfig,
} from 'components/dashboard/SummaryDashboard';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Send,
} from 'lucide-react';
import { useState } from 'react';

type CameraTypeFilter = 'all' | 'vitronics' | 'arh';

const Dashboard = () => {
  const [cameraType, setCameraType] = useState<CameraTypeFilter>('all');
  const selectedCameraQueryParams =
    cameraType === 'all' ? undefined : { cameraType };

  return (
    <SummaryDashboard
      title="Offences Dashboard"
      subtitle="Traffic Enforcement - SMS Notifications"
      cardColumns="md:grid-cols-2 xl:grid-cols-5"
      queryParams={selectedCameraQueryParams}
      dateInputType="datetime-local"
      headerControls={
        <label className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <span className="text-xs font-medium text-gray-400">Camera</span>
          <select
            value={cameraType}
            onChange={(e) => setCameraType(e.target.value as CameraTypeFilter)}
            className="text-sm bg-transparent border-none outline-none text-gray-700"
          >
            <option value="all">All</option>
            <option value="vitronics">Vitronics</option>
            <option value="arh">ARH</option>
          </select>
        </label>
      }
      buildCards={(summary, { pct }): SummaryCardConfig[] => {
        const totalFines = summary?.totalFines ?? 0;
        const smsSent = summary?.smsSent ?? 0;
        const smsNotSent = summary?.smsNotSent ?? 0;
        const smsPending = summary?.smsPending ?? 0;
        const smsFailed = summary?.smsFailed ?? 0;

        return [
          {
            label: 'Total Fines',
            value: totalFines,
            icon: <FileText size={22} className="text-gray-600" />,
            iconBg: 'bg-gray-100',
            valueColor: 'text-gray-900',
            borderColor: 'border-gray-200',
          },
          {
            label: 'SMS Sent',
            value: smsSent,
            sublabel: `${pct(smsSent, totalFines)}% of total`,
            icon: <CheckCircle size={22} className="text-emerald-600" />,
            iconBg: 'bg-emerald-50',
            valueColor: 'text-emerald-700',
            borderColor: 'border-emerald-200',
          },
          {
            label: 'SMS Not Sent',
            value: smsNotSent,
            sublabel: `${pct(smsNotSent, totalFines)}% of total`,
            icon: <Send size={22} className="text-sky-600" />,
            iconBg: 'bg-sky-50',
            valueColor: 'text-sky-700',
            borderColor: 'border-sky-200',
          },
          {
            label: 'SMS Pending',
            value: smsPending,
            sublabel: `${pct(smsPending, totalFines)}% of total`,
            icon: <Clock size={22} className="text-amber-600" />,
            iconBg: 'bg-amber-50',
            valueColor: 'text-amber-700',
            borderColor: 'border-amber-200',
          },
          {
            label: 'SMS Failed',
            value: smsFailed,
            sublabel: `${pct(smsFailed, totalFines)}% of total`,
            icon: <AlertTriangle size={22} className="text-red-600" />,
            iconBg: 'bg-red-50',
            valueColor: 'text-red-700',
            borderColor: 'border-red-200',
          },
        ];
      }}
      buildBreakdownGroups={(summary, { pct }): BreakdownGroup[] => {
        const totalFines = summary.totalFines ?? 0;
        const smsSent = summary.smsSent ?? 0;
        const smsNotSent = summary.smsNotSent ?? 0;
        const smsPending = summary.smsPending ?? 0;
        const smsFailed = summary.smsFailed ?? 0;

        const sentPct = pct(smsSent, totalFines);
        const pendingNotSentPct = pct(smsPending, smsNotSent);
        const failedNotSentPct = pct(smsFailed, smsNotSent);

        return [
          {
            trackBg: 'bg-sky-500',
            segments: [{ color: 'bg-emerald-500', width: sentPct }],
            items: [
              {
                dot: 'bg-emerald-500',
                label: 'Sent',
                value: smsSent,
                pctVal: sentPct,
              },
              {
                dot: 'bg-sky-500',
                label: 'Not Sent',
                value: smsNotSent,
                pctVal: pct(smsNotSent, totalFines),
              },
            ],
          },
          {
            title: 'Not sent details (% of not sent)',
            trackBg: 'bg-gray-100',
            segments: [
              { color: 'bg-amber-500', width: pendingNotSentPct },
              {
                color: 'bg-red-500',
                left: pendingNotSentPct,
                width: failedNotSentPct,
              },
            ],
            items: [
              {
                dot: 'bg-amber-500',
                label: 'Pending',
                value: smsPending,
                pctVal: pendingNotSentPct,
              },
              {
                dot: 'bg-red-500',
                label: 'Failed',
                value: smsFailed,
                pctVal: failedNotSentPct,
              },
            ],
          },
        ];
      }}
    />
  );
};

export default Sentry.withProfiler(Dashboard);
